import { Button } from '@components/ui/button';
import { Textarea } from '@components/ui/textarea';
import { Check, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface EditableSummaryProps {
  value: string;
  onSave: (value: string) => void;
  onCancel: () => void;
  placeholder?: string;
  isLoading?: boolean;
}

// Max visible height (px) before the editor scrolls instead of growing further.
// Long summaries (roughly 1000+ words) stay editable but are contained in a
// scrollable box rather than pushing the rest of the page down. Tunable.
const MAX_HEIGHT_PX = 600;

export const EditableSummary: React.FC<EditableSummaryProps> = ({
  value,
  onSave,
  onCancel,
  placeholder = 'Enter summary...',
  isLoading = false,
}) => {
  const [editValue, setEditValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Grow to fit content up to MAX_HEIGHT_PX, then scroll.
  const resize = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT_PX ? 'auto' : 'hidden';
  };

  useEffect(() => {
    // Auto-focus and size on mount
    if (textareaRef.current) {
      textareaRef.current.focus();
      resize(textareaRef.current);
    }
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // No length cap — long summaries must remain fully editable.
    setEditValue(e.target.value);
    resize(e.target);
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    onSave(trimmedValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Save on Ctrl/Cmd + Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
    // Cancel on Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const wordCount = editValue.trim() ? editValue.trim().split(/\s+/).length : 0;

  return (
    <div className="space-y-2">
      <Textarea
        ref={textareaRef}
        value={editValue}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="min-h-[60px] max-h-[600px] resize-none overflow-y-auto text-xs! md:text-xs! font-normal! leading-relaxed"
        disabled={isLoading}
      />
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{wordCount} words</span>
        <div className="flex gap-1">
          <Button
            type="button"
            size="sm"
            variant="default"
            onClick={handleSave}
            disabled={isLoading || editValue.trim() === value}
            className="h-7 px-2"
          >
            <Check className="w-3 h-3 mr-1" />
            Save
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="h-7 px-2"
          >
            <X className="w-3 h-3 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
